import { faChevronRight as fasChevronRight } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { AbstractStepProps } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { getPromoText } from "@utils/product-marketing-text";
import autobind from "autobind-decorator";
import { Button } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import type { RetailAccountResponseItem } from "../../Calculator/types";

type StepFields = {};

interface Props extends AbstractStepProps {
	results: RetailAccountResponseItem[];
	onSelect?: (index: number, result: RetailAccountResponseItem) => void;
	variant?: string;
}

export class Result extends AbstractStep<StepFields, Props> {
	protected renderContent(): ReactNode {
		return (
			<>
				<Card.Header className="result-header">
					<Card.Title className="font-weight-bold text-center">Legalacsonyabb összköltség évente</Card.Title>
				</Card.Header>
				<Card.Body>{this.renderResults()}</Card.Body>
			</>
		);
	}

	private renderResults(): ReactNode {
		const { results } = this.props;

		if (!results) {
			return (
				<>
					<p className="text-center">
						Sajnos a megadott adatok alapján nem találtunk számodra alkalmas bankszámla csomagot.
					</p>
					<p className="text-center">Kérlek ellenőrizd az általad megadott értékeket!</p>
				</>
			);
		}

		return results.map((result, index) => this.renderResult(index, result));
	}

	private getResultClassName(result: RetailAccountResponseItem): string {
		const classNames = ["position-relative", "mb-2"];

		if (result.marketing?.cssRule) {
			classNames.push(result.marketing?.cssRule);
		}

		return classNames.join(" ");
	}

	private renderResult(index: number, result: RetailAccountResponseItem): ReactNode {
		return (
			<OverlayTrigger
				placement="top"
				delay={{ show: 250, hide: 400 }}
				overlay={
					<Tooltip id="button-tooltip">
						{result.bank.name} - {result.name}
					</Tooltip>
				}
			>
				<div key={index} className={this.getResultClassName(result)}>
					<div className="retail-account-result-item">
						<div className="d-flex">
							<div className="result-data d-flex rounded mr-2">
								<div className="number-field bg-medium-gray rounded-left">
									<div className="number">{index + 1}</div>
								</div>
								<div className="result-info d-flex flex-wrap px-0 mx-0 h-100">
									<div className="w-100 product-name py-1">
										<strong className="text-truncate ml-2">
											{result.bank.name} - {result.name.replace(/\((.*?)\)/g, "")}
										</strong>
									</div>
									<div className="w-50 text-center align-items-center py-1 px-2 border-right-grey d-flex justify-content-between">
										<div
											className={`result-years text-${
												result.firstYear.monthlyTotalFee > 0 ? "primary" : "success"
											}`}
										>
											<strong>{formatText(result.firstYear.monthlyTotalFee * 12, "ft")}</strong>
										</div>
										<div>1. év</div>
									</div>
									<div className="w-50 text-center align-items-center py-1 px-2 d-flex justify-content-between">
										<div
											className={`result-years text-${
												result.firstYear.monthlyTotalFee > 0 ? "primary" : "success"
											}`}
										>
											<strong>{formatText(result.secondYear.monthlyTotalFee * 12, "ft")}</strong>
										</div>
										<div>2. év</div>
									</div>
									<div className="w-100 py-1 px-2 bg-medium-gray marketing-text-field">
										{(!!result.marketing?.cssRule &&
											getPromoText(result.marketing?.cssRule)?.smallCalcText) ||
											""}
									</div>
								</div>
							</div>
							{this.renderButton(index, result)}
						</div>
					</div>
				</div>
			</OverlayTrigger>
		);
	}

	private renderButton(index: number, result: RetailAccountResponseItem): ReactNode {
		return (
			<Button
				className="text-uppercase stretched-link px-3"
				size="sm"
				outlined={this.getBankByVariant() !== result.bank.code}
				onClick={() => this.onSelect(index, result)}
				icon={<FontAwesomeIcon icon={fasChevronRight} />}
			/>
		);
	}

	private getBankByVariant(): string {
		const { variant } = this.props;

		// A többi variant 1:1 a bank neve, csak CIB esetén különböző
		if (variant === "cib-eco-lead") {
			return "cib";
		}

		return variant;
	}

	@autobind
	private onSelect(index: number, result: RetailAccountResponseItem): void {
		this.props.onSelect(index, result);

		this.onStep("lead-form");
	}

	protected getComponentName(): string {
		return "Result";
	}
}
